import { useMemo, useRef, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import AthleteService from "services/AthleteService";
import BulkUploadService from "services/BulkUploadService";
import ArchivedAthletesProvider from "providers/ArchivedAthletesProvider";
import AthleteStoreContext from "./contexts/AthleteStoreContext";
import { ArchivedAthlete, Athlete } from "types/athlete";
import AthleteActionsContext from "./contexts/AthleteActionsContext";
import { AthleteActions } from "./AthletesProvider.types";
import useNaturalSort from "hooks/useNaturalSort";
import { firstBy } from "thenby";
import useStatus from "hooks/useStatus";
import AthleteStatusContext from "./contexts/AthleteStatusContext";

function AthletesProvider(props: { children?: React.ReactNode }) {
  const apiCallRef = useRef(false);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const { status, setStatus } = useStatus();
  const naturalCompare = useNaturalSort();

  const handleFetchAll = useCallback(async () => {
    if (apiCallRef.current) return;

    apiCallRef.current = true;

    try {
      const result = await AthleteService.getAll();
      setAthletes(result);
      apiCallRef.current = false;
      setStatus("success");
    } catch (error) {
      apiCallRef.current = false;
      setStatus("error");
      throw error;
    }
  }, [setStatus]);

  const handleFetchById = useCallback(async (athleteId: string) => {
    try {
      const fetchedAthlete = await AthleteService.getById(athleteId);
      setAthletes((prevAthletes) => [...prevAthletes.filter((athlete) => athlete.id !== athleteId), fetchedAthlete]);
    } catch (error: any) {}
  }, []);

  const handleCancelEmailInvitation = useCallback(async (athleteId: string, email?: string | null) => {
    const emailToCancel = email ?? null;
    await AthleteService.cancelInvite(athleteId, emailToCancel);
    setAthletes((prevAthletes) =>
      prevAthletes.map((athlete) => {
        if (athlete.id !== athleteId) return athlete;
        const updatedAthlete: Athlete = {
          ...athlete,
          email: emailToCancel,
          pendingEmail: null,
          pendingEmailAddedAt: null,
        };
        return updatedAthlete;
      })
    );
  }, []);

  const handleUpdateAthleteEmail = useCallback(async (athleteId: string, email: string) => {
    await AthleteService.updateEmail(athleteId, email);
    setAthletes((prevAthletes) =>
      prevAthletes.map((athlete) => {
        if (athlete.id !== athleteId) return athlete;

        const updatedAthlete: Athlete = {
          ...athlete,
          pendingEmail: email,
          pendingEmailAddedAt: new Date().toISOString(),
        };
        return updatedAthlete;
      })
    );
  }, []);

  const handleCreateAthlete = useCallback(
    async (athlete: Athlete) => {
      const result = await AthleteService.createV2(athlete);

      await handleFetchById(result.id);

      return result.id;
    },
    [handleFetchById]
  );

  const handleUpdateAthlete = useCallback(
    async (updatedAthlete: Athlete) => {
      const result = await AthleteService.updateV2(updatedAthlete);
      await handleFetchById(result.id);
      return result.id;
    },
    [handleFetchById]
  );

  const handleDeleteAthlete = useCallback(async (athleteId: string) => {
    await AthleteService.delete(athleteId);
    setAthletes((prevAthletes) => prevAthletes.filter((athlete) => athlete.id !== athleteId));
  }, []);

  const bulkAssignGroups = useCallback(async (bulkData: { id: string; groupIds: string[] }[]) => {
    let backupPrevAthletes: Athlete[] = [];

    try {
      setAthletes((prevAthletes) => {
        // create a backup of prevAthletes to be set only if the API call errors.
        backupPrevAthletes = prevAthletes.slice(0);

        return prevAthletes.map((athlete) => {
          const updatedGroupIds = bulkData.find(({ id }) => id === athlete.id)?.groupIds;

          if (updatedGroupIds) return { ...athlete, groupIds: updatedGroupIds };

          return athlete;
        });
      });
      await AthleteService.bulkAssignGroups(bulkData);
    } catch (error) {
      // revert athletes on API error
      setAthletes(backupPrevAthletes);
      throw error;
    }
  }, []);

  const handleToggleArchiveAthlete = useCallback(async (athleteId: string, isArchived: boolean) => {
    await AthleteService.archive(athleteId, isArchived);

    setAthletes((prevAthletes) =>
      prevAthletes.map((athlete) => {
        if (athlete.id !== athleteId) {
          return athlete;
        }

        return { ...athlete, isArchived };
      })
    );
  }, []);

  const bulkUpload = useMemo(
    () => ({
      create: async (newAthletes: Athlete[]) => {
        await BulkUploadService.bulkCreate(newAthletes);
        await handleFetchAll();
      },
    }),
    [handleFetchAll]
  );

  useEffect(() => {
    handleFetchAll();
  }, [handleFetchAll]);

  const athleteActions: AthleteActions = useMemo(
    () => ({
      fetchAll: handleFetchAll,
      fetchById: handleFetchById,
      create: handleCreateAthlete,
      update: handleUpdateAthlete,
      delete: handleDeleteAthlete,
      toggleArchived: handleToggleArchiveAthlete,
      cancelEmail: handleCancelEmailInvitation,
      updateEmail: handleUpdateAthleteEmail,
      bulkUpload,
      bulkAssignGroups,
    }),
    [
      bulkAssignGroups,
      bulkUpload,
      handleCancelEmailInvitation,
      handleCreateAthlete,
      handleDeleteAthlete,
      handleFetchAll,
      handleFetchById,
      handleToggleArchiveAthlete,
      handleUpdateAthlete,
      handleUpdateAthleteEmail,
    ]
  );

  const unarchivedAthletes = useMemo(() => {
    const unarchived = athletes.filter((athlete) => !athlete.isArchived);
    const sortedAthletes = unarchived.sort(
      firstBy("firstName", { cmp: naturalCompare }).thenBy("lastName", { cmp: naturalCompare })
    );
    return sortedAthletes;
  }, [athletes, naturalCompare]);

  const archivedAthletes = useMemo(() => {
    const archived = athletes.filter((athlete) => athlete.isArchived) as ArchivedAthlete[];
    const sortedAthletes = archived.sort(
      firstBy("firstName", { cmp: naturalCompare }).thenBy("lastName", { cmp: naturalCompare })
    );
    return sortedAthletes;
  }, [athletes, naturalCompare]);

  return (
    <AthleteActionsContext.Provider value={athleteActions}>
      <AthleteStoreContext.Provider value={unarchivedAthletes}>
        <ArchivedAthletesProvider athletes={archivedAthletes}>
          <AthleteStatusContext.Provider value={status}>{props.children}</AthleteStatusContext.Provider>
        </ArchivedAthletesProvider>
      </AthleteStoreContext.Provider>
    </AthleteActionsContext.Provider>
  );
}

AthletesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AthletesProvider;
